import type { CSSProperties } from 'react';
import { buttonGroupClasses } from '@mui/material/ButtonGroup';
import { blueGrey, grey, orange } from '@mui/material/colors';
import { alpha, createTheme, type PaletteMode, type PaletteOptions, type SxProps, type Theme, type ThemeOptions } from '@mui/material/styles';
import type { SystemCssProperties } from '@mui/system/styleFunctionSx';
import { deepmerge } from '@mui/utils';
import type {} from '@mui/x-data-grid-premium/themeAugmentation';
import EnvoriaLoadingIcon from 'components/v2/EnvoriaLoadingIcon';
import DMSansBoldTTF from 'resources/fonts/DMSans-Bold.ttf';
import DMSansBoldItalicTTF from 'resources/fonts/DMSans-BoldItalic.ttf';
import DMSansItalicTTF from 'resources/fonts/DMSans-Italic.ttf';
import DMSansRegularTTF from 'resources/fonts/DMSans-Regular.ttf';
import GTAmericaExtendedRegularTTF from 'resources/fonts/GTAmerica-ExtendedRegular.ttf';
import GTAmericaExtendedRegularWOFF from 'resources/fonts/GTAmerica-ExtendedRegular.woff';
import GTAmericaExtendedRegularWOFF2 from 'resources/fonts/GTAmerica-ExtendedRegular.woff2';
import type { UserTheme } from 'types/theme';

const DMSansName = '"DM Sans"';
const GTAmericaName = '"GT America"';

const GTAmericaExtendedRegular = {
   fontFamily: GTAmericaName,
   fontStyle: 'normal',
   fontWeight: 'normal',
   src: `
    url(${GTAmericaExtendedRegularWOFF2}) format('woff2'),
    url(${GTAmericaExtendedRegularWOFF}) format('woff'),
    url(${GTAmericaExtendedRegularTTF}) format('truetype')
  `,
};

const DMSansRegular = {
   fontFamily: DMSansName,
   fontStyle: 'normal',
   fontWeight: 'normal',
   src: `
    url(${DMSansRegularTTF}) format('truetype')
  `,
};

const DMSansBold = {
   fontFamily: DMSansName,
   fontStyle: 'normal',
   fontWeight: 'bold',
   src: `
    url(${DMSansBoldTTF}) format('truetype')
  `,
};

const DMSansItalic = {
   fontFamily: DMSansName,
   fontStyle: 'italic',
   fontWeight: 'normal',
   src: `
    url(${DMSansItalicTTF}) format('truetype')
  `,
};

const DMSansBoldItalic = {
   fontFamily: DMSansName,
   fontStyle: 'italic',
   fontWeight: 'bold',
   src: `
    url(${DMSansBoldItalicTTF}) format('truetype')
  `,
};

const darkModePalette: PaletteOptions = {
   // customGrey: {
   //    main: grey[300],
   //    dark: grey[400],
   // },
   background: {
      default: '#111315',
      paper: '#141821',
   },
   blueGrey,
   orange,
   text: {
      primary: '#FFFFFF',
      secondary: '#ADB4BD',
   },
   fieldLabel: {
      main: '#FFFFFF',
   },
   button: {
      border: {
         main: 'rgba(255, 255, 255, 0.23)',
      },
   },
   primary: {
      light: '#FFFFFF',
      main: '#336CFF',
      dark: '#141821',
      opaque: '#282f3d',
      menu: '#909CB5',
   },
   secondary: {
      main: '#59DB7E',
      menu: '#00E0D7',
   },
   error: {
      main: '#EB5757',
   },
   warning: {
      main: '#F2C94C',
   },
   success: {
      main: '#59DB7E',
   },
   info: {
      main: '#3971FF',
   },
};

const lightModePalette: PaletteOptions = {
   // customGrey: {
   //    main: grey[300],
   //    dark: grey[600],
   // },
   background: { default: '#F4F8FD', paper: '#FFFFFF' },
   blueGrey,
   orange,
   text: {
      primary: '#0A284B',
      secondary: '#3B618A',
   },
   fieldLabel: {
      main: '#141821',
   },
   button: {
      border: {
         main: '#CCD8E1',
      },
   },
   primary: {
      light: '#7AA8D8',
      main: '#336CFF',
      dark: '#141821',
      opaque: '#282f3d',
      menu: '#909CB5',
   },
   secondary: {
      main: '#59DB7E',
      menu: '#00E0D7',
   },
   error: {
      main: '#EB5757',
   },
   warning: {
      main: '#F2C94C',
   },
   success: {
      main: '#59DB7E',
   },
   info: {
      main: '#3971FF',
   },
};

const getModeThemePalette = (mode: PaletteMode) =>
   mode === 'light'
      ? {
           ...lightModePalette,
        }
      : {
           ...darkModePalette,
        };

export default function envoriaTheme(mode: PaletteMode, userTheme: UserTheme | null = null) {
   const modeThemePalette = getModeThemePalette(mode);
   const userThemeModePalette = userTheme ? userTheme?.[mode]?.palette : {};

   const mergedThemePalette = deepmerge(modeThemePalette, userThemeModePalette);

   const baseConfig: ThemeOptions = {
      palette: {
         mode,
         ...mergedThemePalette,
      },
      typography: {
         fontFamily: [DMSansName, GTAmericaName, 'Roboto', 'sans-serif', '"Apple Color Emoji"', '"Segoe UI Emoji"', '"Segoe UI Symbol"'].join(','),
         h1: {
            fontFamily: GTAmericaName,
         },
         h2: {
            fontFamily: GTAmericaName,
         },
         h3: {
            fontFamily: GTAmericaName,
         },
         h4: {
            fontFamily: GTAmericaName,
         },
         h5: {
            fontFamily: GTAmericaName,
         },
         h6: {
            fontFamily: DMSansName,
         },
         button: {
            fontFamily: DMSansName,
         },
         subtitle1: {
            fontFamily: DMSansName,
         },
         subtitle2: {
            fontFamily: DMSansName,
         },
         body1: {
            fontSize: '0.8rem',
            fontFamily: DMSansName,
         },
         body2: {
            fontFamily: DMSansName,
         },
         paragraph: {
            fontFamily: DMSansName,
         },
         caption: {
            fontFamily: DMSansName,
         },
         overline: {
            fontFamily: DMSansName,
         },
      },
   };

   let theme = createTheme(baseConfig);
   theme = createTheme(theme, {
      palette: {
         customGrey: theme.palette.augmentColor({
            name: 'customGrey',
            color:
               mode === 'dark'
                  ? { main: grey[300], dark: grey[400] }
                  : {
                       main: grey[300],
                       dark: grey[600],
                    },
         }),
      },
   });

   const textFieldConfig = {
      styleOverrides: {
         root: {
            color: theme.palette.text.primary, // each theme has its own text.primary color
            '& svg:not(.Mui-error)': {
               fill: theme.palette.mode === 'dark' ? theme.palette.primary.menu : theme.palette.blueGrey[400],
               fontSize: 20,
            },
            '& .MuiFormLabel-root': {
               '&.MuiInputLabel-root:not(.Mui-disabled)': {
                  color: theme.palette.fieldLabel.main,
               },
               '&.MuiInputLabel-shrink': {
                  fontSize: '1rem',
               },
               '&:not(.Mui-focused) &:not(.Mui-disabled)': {
                  color: theme.palette.mode === 'dark' ? theme.palette.customGrey.dark : 'inherit',
               },
               '&.Mui-focused': {
                  color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.info.main,
               },
               '&.Mui-error': {
                  color: theme.palette.error.main,
               },
            },

            '& .Mui-error.Mui-focused > fieldset': {
               border: `2px solid ${theme.palette.error.main}`,
            },

            '& input': {
               // color: theme.palette.text.primary,
               /* Chrome, Safari, Edge, Opera */
               '&:-webkit-autofill': {
                  WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.paper} inset`,
               },
               '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
               },
            },

            /* Firefox */
            '& input[type=number]': {
               MozAppearance: 'textfield',
            },

            '& .MuiFormHelperText-root.Mui-error': {
               marginBottom: '12px',
            },

            '& fieldset > legend': {
               fontSize: '0.76rem',
            },
         },
      },
   };

   const inputBaseConfig = {
      styleOverrides: {
         root: {
            /* Firefox */
            '& input[type=number]': {
               MozAppearance: 'textfield',
            },
            '& input': {
               /* Chrome, Safari, Edge, Opera */
               '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
               },
            },
         },
      },
   };

   const isDarkMode = theme.palette.mode === 'dark';
   const ckTextPrimary = theme.palette.text.primary;
   const ckTextSecondary = theme.palette.text.secondary;
   const ckUiBackground = isDarkMode ? theme.palette.background.default : theme.palette.background.paper;
   const ckSurfaceBackground = theme.palette.background.paper;
   const ckBorderColor = theme.palette.divider;
   const ckHoverBackground = isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(10, 40, 75, 0.06)';
   const ckActiveBackground = isDarkMode ? 'rgba(51, 108, 255, 0.24)' : 'rgba(51, 108, 255, 0.12)';
   const ckDisabledBackground = isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(10, 40, 75, 0.04)';
   const ckDisabledText = isDarkMode ? 'rgba(255, 255, 255, 0.50)' : 'rgba(10, 40, 75, 0.45)';
   const ckTooltipBackground = isDarkMode ? '#0F131B' : '#1D2D3D';
   const ckLinkColor = isDarkMode ? '#8EC5FF' : theme.palette.info.main;

   const ckDarkAnnotationVariables = isDarkMode
      ? {
           '--ck-annotation-wrapper-background-color': ckSurfaceBackground,
           '--ck-color-annotation-info': ckTextSecondary,
           '--ck-color-comment-background': ckHoverBackground,
           '--ck-comment-separator-color': ckBorderColor,
           '--ck-color-comment-marker': 'rgba(242, 222, 2, 0.24)',
           '--ck-color-comment-marker-active': 'rgba(242, 222, 2, 0.40)',
           '--ck-thread-header-background-color': 'rgba(242, 222, 2, 0.12)',
           '--ck-thread-header-active-background-color': 'rgba(242, 222, 2, 0.20)',
           '--ck-thread-unlinked-background-color': ckHoverBackground,
           '--ck-thread-unlinked-active-background-color': ckActiveBackground,
        }
      : {};

   const ckDarkRevisionHistoryVariables = isDarkMode
      ? {
           '--ck-revision-history-revision-shadow': `0 1px 1px 1px ${ckBorderColor}`,
           '--ck-revision-history-revision-background-color': ckSurfaceBackground,
           '--ck-revision-history-revision-selected-background-color': ckSurfaceBackground,
           '--ck-revision-history-revision-highlighted-background-color': ckHoverBackground,
           '--ck-revision-history-revision-highlighted-hover-background-color': ckActiveBackground,
           '--ck-revision-history-loading-overlay-visible-background-color': ckSurfaceBackground,
        }
      : {};

   // CKEditor declares these on every `.ck` element and pairs `--ck-color-base-active` with
   // `--ck-color-base-background` text, which assumes a solid active colour rather than our tint.
   const ckRevisionHistoryVariables = {
      ...ckDarkRevisionHistoryVariables,
      '--ck-revision-history-revision-selected-text-color': ckTextPrimary,
      '--ck-revision-history-revision-selected-author-text-color': ckTextPrimary,
      '--ck-revision-history-revision-selected-date-text-color': ckTextSecondary,
      '--ck-revision-history-revision-selected-input-text-color': ckTextPrimary,
   };

   const ckCaptionBackground = isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#F6FAFF';
   const ckShadowDrop = isDarkMode ? 'rgba(0, 0, 0, 0.40)' : 'rgba(21, 56, 92, 0.12)';

   // CKEditor 49 derives these tokens from the legacy names on `:root`, so the legacy overrides below,
   // which apply to the editor containers only, stop short of them. Setting them here keeps the editor
   // UI on our colours.
   const ckDerivedThemeVariables = {
      '--ck-color-surface-canvas': ckSurfaceBackground,
      '--ck-color-surface-container': ckSurfaceBackground,
      '--ck-color-surface-control': ckSurfaceBackground,
      '--ck-color-surface-inverse': ckTextPrimary,
      '--ck-color-text-primary': ckTextPrimary,
      '--ck-color-text-secondary': ckTextSecondary,
      '--ck-color-base-text-light': ckTextSecondary,
      '--ck-color-engine-placeholder-text': ckTextSecondary,
      '--ck-color-interactive-selected-text': ckTextPrimary,
      '--ck-button-on-text-color': ckTextPrimary,
      '--ck-annotation-icon-color': ckTextPrimary,
      '--ck-annotation-info-name-color': ckTextPrimary,
      '--ck-annotation-info-icon-color': ckTextSecondary,
      '--ck-annotation-info-time-color': ckTextSecondary,
      '--ck-annotation-counter-color': ckTextSecondary,
      '--ck-annotation-counter-hover-color': ckTextPrimary,
      '--ck-annotation-counter-focus-color': ckLinkColor,
      '--ck-thread-header-button-hover-background': ckHoverBackground,
      '--ck-comment-input-background-color': ckSurfaceBackground,
      '--ck-document-outline-item-default-color': ckTextPrimary,
      '--ck-document-outline-item-hover-state-color': ckTextPrimary,
      '--ck-document-outline-item-active-color': ckLinkColor,
      '--ck-suggestion-box-format-border-color': theme.palette.info.main,
      '--ck-color-table-column-resizer-hover': ckActiveBackground,
      '--ck-color-widget-type-around-button': theme.palette.info.main,
      '--ck-color-widget-type-around-button-active': theme.palette.info.main,
      '--ck-color-widget-type-around-button-hover': theme.palette.info.main,
      '--ck-color-widget-type-around-button-blurred-editable': ckBorderColor,
      '--ck-color-widget-type-around-button-icon': ckSurfaceBackground,
      '--ck-color-text-inverse': ckSurfaceBackground,
      '--ck-color-border-control': ckBorderColor,
      '--ck-color-border-container': ckBorderColor,
      '--ck-color-divider': ckBorderColor,
      '--ck-focus-ring': `1px solid ${theme.palette.info.main}`,
      '--ck-input-focus-border-color': theme.palette.info.main,
      '--ck-interactive-focus-ring': `1px solid ${theme.palette.info.main}`,
      '--ck-interactive-focus-border-color': theme.palette.info.main,
      '--ck-button-focus-border-color': theme.palette.info.main,
      '--ck-interactive-focus-shadow': `0 0 0 3px ${alpha(theme.palette.info.main, 0.22)}`,
      '--ck-dialog-border': `var(--ck-border-width-surface) solid ${ckBorderColor}`,
      '--ck-shadow-md': `0 1px 2px 1px ${ckShadowDrop}`,
      '--ck-input-border': `var(--ck-border-width-control) solid ${ckBorderColor}`,
      '--ck-toolbar-border': `var(--ck-border-width-surface) solid ${ckBorderColor}`,
      '--ck-dropdown-panel-border': `var(--ck-border-width-surface) solid ${ckBorderColor}`,
      '--ck-balloon-panel-border': `var(--ck-border-width-surface) solid ${ckBorderColor}`,
      '--ck-border-surface': `var(--ck-border-width-surface) solid ${ckBorderColor}`,
   };

   const ckThemeVariables = {
      ...ckDarkAnnotationVariables,
      ...ckDerivedThemeVariables,
      '--ck-border-radius': '4px',
      '--ck-content-font-family': 'Calibri, Carlito, sans-serif',
      '--ck-content-font-size': '12pt',
      '--ck-content-line-height': '1.6',
      '--ck-content-list-marker-font-family': 'var(--ck-content-font-family)',
      '--ck-content-list-marker-font-size': 'var(--ck-content-font-size)',
      '--ck-content-list-marker-line-height': 'var(--ck-content-line-height)',
      '--ck-content-list-marker-color': 'var(--ck-content-font-color)',
      '--ck-content-font-color': ckTextPrimary,
      '--ck-color-text': ckTextPrimary,
      '--ck-color-base-text': ckTextPrimary,
      '--ck-comment-content-font-color': ckTextPrimary,
      '--ck-color-base-background': ckSurfaceBackground,
      '--ck-color-base-foreground': ckUiBackground,
      '--ck-color-base-border': ckBorderColor,
      '--ck-color-base-active': ckActiveBackground,
      '--ck-color-base-active-focus': ckActiveBackground,
      '--ck-color-focus-border': theme.palette.info.main,
      '--ck-color-shadow-drop': ckShadowDrop,
      '--ck-color-shadow-inner': isDarkMode ? 'rgba(0, 0, 0, 0.16)' : 'rgba(21, 56, 92, 0.08)',
      '--ck-color-button-default-background': ckUiBackground,
      '--ck-color-button-default-hover-background': ckHoverBackground,
      '--ck-color-button-default-active-background': ckActiveBackground,
      '--ck-color-button-default-active-shadow': 'transparent',
      '--ck-color-button-default-disabled-background': ckUiBackground,
      '--ck-color-button-on-background': isDarkMode ? 'rgba(255, 255, 255, 0.14)' : 'rgba(10, 40, 75, 0.08)',
      '--ck-color-button-on-hover-background': isDarkMode ? 'rgba(255, 255, 255, 0.20)' : 'rgba(10, 40, 75, 0.14)',
      '--ck-color-button-on-active-background': isDarkMode ? 'rgba(255, 255, 255, 0.22)' : 'rgba(10, 40, 75, 0.16)',
      '--ck-color-button-on-active-shadow': 'transparent',
      '--ck-color-button-on-disabled-background': ckDisabledBackground,
      '--ck-color-button-action-background': theme.palette.secondary.main,
      '--ck-color-button-action-hover-background': theme.palette.secondary.main,
      '--ck-color-button-action-active-background': theme.palette.secondary.main,
      '--ck-color-button-action-active-shadow': 'transparent',
      '--ck-color-button-action-disabled-background': ckDisabledBackground,
      '--ck-color-button-action-text': isDarkMode ? '#111315' : '#FFFFFF',
      '--ck-color-button-save': theme.palette.success.main,
      '--ck-color-button-cancel': theme.palette.error.main,
      '--ck-color-dropdown-panel-background': ckSurfaceBackground,
      '--ck-color-dropdown-panel-border': ckBorderColor,
      '--ck-color-dialog-background': ckSurfaceBackground,
      '--ck-color-dialog-form-header-border': ckBorderColor,
      '--ck-color-split-button-hover-background': ckHoverBackground,
      '--ck-color-split-button-hover-border': ckBorderColor,
      '--ck-color-input-background': ckSurfaceBackground,
      '--ck-color-input-border': ckBorderColor,
      '--ck-color-input-text': ckTextPrimary,
      '--ck-color-input-disabled-background': ckDisabledBackground,
      '--ck-color-input-disabled-border': ckBorderColor,
      '--ck-color-input-disabled-text': ckDisabledText,
      '--ck-color-labeled-field-label-background': ckSurfaceBackground,
      '--ck-color-list-background': ckSurfaceBackground,
      '--ck-color-list-button-hover-background': ckHoverBackground,
      '--ck-color-list-button-on-background': ckActiveBackground,
      '--ck-color-list-button-on-background-focus': ckActiveBackground,
      '--ck-color-list-button-on-text': ckTextPrimary,
      '--ck-balloon-panel-background-color': ckSurfaceBackground,
      '--ck-balloon-panel-border-color': ckBorderColor,
      '--ck-color-toolbar-background': ckUiBackground,
      '--ck-color-toolbar-border': ckBorderColor,
      '--ck-color-tooltip-background': ckTooltipBackground,
      '--ck-color-tooltip-text': '#FFFFFF',
      '--ck-content-color-image-caption-background': ckCaptionBackground,
      '--ck-content-color-image-caption-text': ckTextSecondary,
      '--ck-content-color-table-caption-background': ckCaptionBackground,
      '--ck-content-color-table-caption-text': ckTextSecondary,
      '--ck-color-widget-blurred-border': ckBorderColor,
      '--ck-color-widget-hover-border': theme.palette.info.main,
      '--ck-color-widget-editable-focus-background': ckSurfaceBackground,
      '--ck-color-link-default': ckLinkColor,
   };

   const extension: ThemeOptions = {
      shadows: {
         ...theme.shadows,
         1: '0px 2px 2px rgba(21, 56, 92, 0.14), 0px 1px 5px rgba(21, 56, 92, 0.12)',
      },
      mixins: {
         toolbar: {
            minHeight: 70,
            '@media (min-width:600px)': {
               minHeight: 70,
               background: theme.palette.background.paper,
               paddingRight: 24,
            },
            paddingRight: 24,
            background: theme.palette.background.paper,
         },
      },
      components: {
         MuiDialog: {
            defaultProps: {
               slotProps: {
                  paper: {
                     component: 'form',
                     'aria-autocomplete': 'none',
                     sx: {
                        overflow: 'hidden',
                     },
                  },
               },
            },
         },
         MuiCheckbox: {
            defaultProps: {
               disableRipple: true,
            },
         },
         MuiContainer: {
            styleOverrides: {
               root: {
                  paddingTop: theme.spacing(4),
                  paddingBottom: theme.spacing(4),
               },
            },
         },
         MuiCssBaseline: {
            styleOverrides: {
               '@font-face': DMSansRegular,
               fallbacks: [
                  {
                     '@font-face': DMSansItalic,
                  },
                  {
                     '@font-face': DMSansBold,
                  },
                  {
                     '@font-face': DMSansBoldItalic,
                  },
                  {
                     '@font-face': GTAmericaExtendedRegular,
                  },
               ],
               a: {
                  color: theme.palette.mode === 'dark' ? theme.palette.info.main : '',
               },
               '.editor-container, .revision-history, .ck-body-wrapper': ckThemeVariables,
               '.revision-history .ck': ckRevisionHistoryVariables,
               '.revision-history .ck.ck-button.ck-revision-history-ui__back-to-editing': {
                  color: ckTextPrimary,
               },
               // The thread header's reopen button has no icon colour token in CKEditor 49.
               '.ck .ck-thread__header .ck-button': {
                  color: 'var(--ck-annotation-icon-color)',
               },
               '.editor-container .ck.ck-editor__editable a, .revision-history .ck.ck-editor__editable a': {
                  color: 'var(--ck-color-link-default)',
               },
            },
         },
         MuiFormHelperText: {
            styleOverrides: {
               root: {
                  '&.MuiAlert-standardPrimary': {
                     backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : undefined,
                  },
               },
            },
         },
         MuiTabs: {
            styleOverrides: {
               indicator: {
                  backgroundColor: 'transparent',
               },
            },
         },
         MuiTab: {
            defaultProps: {
               disableRipple: true,
            },
            styleOverrides: {
               root: {
                  textTransform: 'none',
                  '&.Mui-selected::after': {
                     content: '""',
                     position: 'absolute',
                     bottom: 0,
                     borderBottom: 'solid 2px',
                     width: '85%',
                  },
               },
            },
         },
         MuiDataGrid: {
            styleOverrides: {
               root: {
                  backgroundColor: theme.palette.background.paper,
               },
               virtualScroller: {
                  zIndex: 1,
               },
               columnHeaderTitle: {
                  fontWeight: 'bold',
               },
               // MUI caps the view popup at 156px but leaves the edit popup unbounded, so the grid body clips long text.
               editLongTextCellPopperContent: {
                  maxHeight: 52 * 3,
                  overscrollBehavior: 'contain',
               },
               longTextCellPopperContent: {
                  overscrollBehavior: 'contain',
               },
               editLongTextCellTextarea: {
                  color: theme.palette.text.primary,
               },
            },
            defaultProps: {
               autosizeOptions: {
                  expand: true,
                  includeOutliers: true,
                  includeHeaders: true,
               },
               slotProps: {
                  toolbar: {
                     csvOptions: {
                        disableToolbarButton: true,
                     },
                     printOptions: {
                        disableToolbarButton: true,
                     },
                  },
               },
               slots: {
                  loadingOverlay: EnvoriaLoadingIcon,
               },
            },
         },
         MuiAppBar: {
            styleOverrides: {
               root: {
                  zIndex: theme.zIndex.drawer + 1,
                  width: `calc(100% - 96px)`,
                  transition: theme.transitions.create(['width', 'margin'], {
                     easing: theme.transitions.easing.sharp,
                     duration: theme.transitions.duration.leavingScreen,
                  }),
                  borderBottomWidth: 1,
                  borderBottomStyle: 'solid',
                  borderBottomColor: theme.palette.grey[400],
               },
               colorPrimary: {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.paper,
               },
            },
         },
         MuiInputBase: inputBaseConfig,
         MuiTextField: textFieldConfig,
         MuiButtonGroup: {
            defaultProps: {
               disableRipple: true,
            },
            variants: [
               {
                  props: { variant: 'outlined' },
                  style: {
                     borderColor: theme?.palette?.button?.border?.main,
                     '&:hover': {
                        borderColor: theme?.palette?.button?.border?.main,
                     },
                     [`& .${buttonGroupClasses.firstButton}:hover`]: {
                        borderColor: theme?.palette?.button?.border?.main,
                     },
                     [`& .${buttonGroupClasses.middleButton}:hover`]: {
                        borderColor: theme?.palette?.button?.border?.main,
                     },
                  },
               },
            ],
         },
         MuiButtonBase: {
            defaultProps: {
               disableRipple: true,
            },
            styleOverrides: {
               root: {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '15px',
                  borderRadius: '4px',
                  padding: '9px 22px',
                  lineHeight: '160%',
               },
            },
         },
         MuiButton: {
            variants: [
               {
                  props: { variant: 'outlined' },
                  style: {
                     color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
                     borderColor: theme?.palette?.button?.border?.main,
                     '&:hover': {
                        borderColor: theme?.palette?.button?.border?.main,
                     },
                  },
               },
               { props: { variant: 'contained', color: 'secondary' }, style: { color: theme.palette.common.white } },
            ],
            defaultProps: {
               disableElevation: true,
               disableRipple: true,
            },
            styleOverrides: {
               root: ({ ownerState }: { ownerState: { variant?: string; color?: string; style?: CSSProperties; sx?: SxProps<Theme> | null } }) => ({
                  fontFamily: DMSansName,
                  fontSize: 'medium',
                  transition: '0.3s',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  textTransform: 'none',
                  borderRadius: 3,
                  minWidth: 123,
                  '&:hover': {
                     opacity: 0.7,
                  },
                  '&$disabled': {
                     cursor: 'not-allowed',
                     pointerEvents: 'auto',
                  },
                  ...(ownerState?.variant === 'contained' &&
                     (ownerState?.color === undefined || ownerState?.color === 'primary') && {
                        '&:hover': {
                           opacity: 0.7,
                           backgroundColor:
                              ownerState.style?.backgroundColor ??
                              (ownerState.sx as SystemCssProperties<Theme>)?.backgroundColor ?? // NOTE: bypassing check as we know it's a SystemCssProperties
                              theme.palette.primary.main,
                        },
                     }),
               }),
               startIcon: {
                  width: 20,
                  height: 20,
               },
               endIcon: {
                  width: 20,
                  height: 20,
               },
               contained: {
                  color: theme.palette.common.white,
               },
            },
         },
         MuiTableCell: {
            styleOverrides: {
               head: {
                  fontFamily: DMSansName,
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.background.paper,
                  textTransform: 'uppercase',
                  '& .MuiTableSortLabel-root, & .MuiTableSortLabel-root.Mui-active': {
                     textTransform: 'uppercase',
                     color: theme.palette.text.secondary,
                  },
               },
               body: {
                  color: theme.palette.text.primary,
               },
            },
         },
         MuiIcon: {
            styleOverrides: {
               root: {
                  // Match 24px = 3 * 2 + 1.125 * 16
                  boxSizing: 'content-box',
                  padding: 3,
                  fontSize: '1.125rem',
               },
            },
         },
         MuiIconButton: {
            styleOverrides: {
               root: ({ ownerState }: { ownerState: { color?: string } }) => ({
                  ...(ownerState.color === 'default' && {
                     color: theme.palette.mode === 'dark' ? theme.palette.primary.menu : theme.palette.blueGrey[400],
                  }),
               }),
            },
         },
         MuiDialogContent: {
            defaultProps: {
               dividers: true,
            },
            // TODO remove once the issue is addressed: https://github.com/mui-org/material-ui/issues/29892
            styleOverrides: {
               root: {
                  paddingTop: `10px !important`,
                  borderTopColor: 'transparent',
               },
            },
         },
         /* MuiPaper: {
         styleOverrides: {
            root: {
            color: theme.palette.mode === 'dark' ? theme.palette.primary.menu : theme.palette.blueGrey[400],
            },
         },
      }, */
         MuiTablePagination: {
            styleOverrides: {
               toolbar: {
                  color: theme.palette.mode === 'dark' ? theme.palette.primary.menu : 'inherit',
                  '& .MuiInputBase-root': {
                     border: `1px solid ${theme.palette.grey[400]}`,
                     borderRadius: '4px',
                     padding: '2px',
                     '& .MuiTablePagination-select': {
                        textAlign: 'initial',
                        textAlignLast: 'initial',
                        color: theme.palette.mode === 'dark' ? theme.palette.common.white : 'inherit',
                     },
                     '& svg': {
                        color: theme.palette.mode === 'dark' ? theme.palette.primary.menu : 'inherit',
                        right: '2px',
                     },
                  },
               },
            },
         },
      },
   };
   return createTheme(theme, extension);
}
